import { useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "../../AiChat/components";

// Questions are written in both languages and follow the language toggle, so a
// click sends the question in the language the answer will come back in.
const SAMPLE_QUESTIONS = [
  {
    category: { en: "Product spec", zh: "產品規格" },
    text: {
      en: "show me the spec of tg4040",
      zh: "TG4040 的規格是什麼",
    },
  },
  {
    category: { en: "Application", zh: "應用場景" },
    text: {
      en: "tell me the application of tg4040",
      zh: "TG4040 可以用在哪些應用",
    },
  },
  {
    category: { en: "Category ranking", zh: "類別找極值" },
    text: {
      en: "Which Thermal Putty has high thermal conductivity?",
      zh: "哪一款 Thermal Putty 的導熱係數最高？",
    },
  },
  {
    category: { en: "Closest competitor", zh: "最接近的競品" },
    text: {
      en: "Find competitors with a thermal conductivity close to TG-A6200",
      zh: "找跟 TG-A6200 導熱係數相近的競品",
    },
  },
  {
    category: { en: "Model vs competitor", zh: "型號對比競品" },
    text: {
      en: "Compare the thermal conductivity of TG-A1780 and Laird Tflex SF16",
      zh: "請比較 TG-A1780 與 Laird 的 Tflex SF16 的 thermal conductivity",
    },
  },
  {
    category: { en: "Comparison chart", zh: "比較圖表" },
    text: {
      en: "Compare TG-A1250 and Laird Tflex SF7",
      zh: "比較 TG-A1250 與 Laird Tflex SF7",
    },
  },
  {
    category: { en: "Brand comparison by use", zh: "依用途比較品牌" },
    text: {
      en: "TGlobal vs Honeywell for CPU thermal interface",
      zh: "高柏與 Honeywell 用在 CPU 散熱介面的比較",
    },
  },
  {
    category: { en: "Product recommendation", zh: "選型推薦" },
    text: {
      en: "Which of your products have good dielectric strength?",
      zh: "有沒有耐電壓好一點的產品推薦？",
    },
  },
  {
    category: { en: "Selection advice", zh: "選材建議" },
    text: {
      en: "How about thermal grease or thermal putty for CPU applications?",
      zh: "CPU 散熱要用導熱膏還是導熱泥？",
    },
  },
  {
    category: { en: "Tech Q&A", zh: "技術問答" },
    text: {
      en: "What is Vapor Chamber?",
      zh: "什麼是均溫板？",
    },
  },
  // Turns the video toggle on when picked — the question alone would run as a
  // normal text lookup and look like the videos were broken.
  {
    category: { en: "Video", zh: "影片" },
    text: {
      en: "How do I apply thermal grease?",
      zh: "導熱膏怎麼用",
    },
    video: true,
  },
];

function Category({ q, language }) {
  return (
    <>
      {q.category[language] ?? q.category.en}
      {q.video && (
        <span className="ml-1 normal-case text-orange-100">
          {language === "zh" ? "· 會開啟影片搜尋" : "· turns on video search"}
        </span>
      )}
    </>
  );
}

export default function SampleQuestions({
  setInput,
  setIncludeVideo,
  language,
  onSelect,
}) {
  const [page, setPage] = useState(0);
  const pageCount = Math.ceil(SAMPLE_QUESTIONS.length / 4);
  const pick = (q, text) => {
    setInput(text);
    // Only ever turns it on: leaving a user's own choice switched off
    // because they picked a non-video sample would be surprising.
    if (q.video) setIncludeVideo(true);
    onSelect();
  };
  return (
    <div className="w-full">
      <h4 className="text-black dark:text-white text-center font-bold text-base md:text-lg mb-1 md:mb-5">
        {language === "zh" ? "範例問題" : "Sample Questions"}
      </h4>
      <p className="text-black/70 dark:text-white/70 text-center text-sm mb-3 md:mb-6">
        <span className="md:hidden">
          {language === "zh" ? "左右滑動查看更多" : "Swipe for more"}
        </span>
        <span className="hidden md:inline">
          {language === "zh"
            ? "點擊任一問題即可開始"
            : "Click any question below to get started"}
        </span>
      </p>

      {/* Phones: one row of compact cards to swipe through, so the chat box
          stays on screen. Wider screens page through a 2×2 grid below. */}
      <div className="md:hidden flex gap-3 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-1">
        {SAMPLE_QUESTIONS.map((q) => {
          const text = q.text[language] ?? q.text.en;
          return (
            <button
              type="button"
              key={q.category.en}
              onClick={() => pick(q, text)}
              className="snap-start shrink-0 w-60 text-left px-4 py-3 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 border border-orange-400/30 text-white shadow-md"
            >
              <span className="block text-[11px] font-semibold uppercase tracking-wide text-orange-100 mb-1">
                <Category q={q} language={language} />
              </span>
              <span className="block text-sm font-medium leading-snug text-white line-clamp-2">
                {text}
              </span>
            </button>
          );
        })}
      </div>

      <div className="hidden md:flex relative items-center gap-4">
        <button
          type="button"
          aria-label={language === "zh" ? "上一頁問題" : "Previous questions"}
          disabled={page === 0}
          onClick={() => setPage(page - 1)}
          className="p-2 rounded-lg text-black dark:text-white hover:bg-black/10 dark:hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeftIcon />
        </button>
        <div className="flex-1 min-w-0 grid grid-cols-2 gap-4">
          {SAMPLE_QUESTIONS.slice(page * 4, page * 4 + 4).map((q) => {
            const text = q.text[language] ?? q.text.en;
            return (
              <button
                type="button"
                key={q.category.en}
                onClick={() => pick(q, text)}
                className="group relative overflow-hidden text-left px-5 py-6 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 border border-orange-400/30 transition-all duration-300 text-white shadow-lg hover:shadow-2xl hover:shadow-orange-500/50 motion-safe:hover:-translate-y-1"
              >
                <span
                  aria-hidden="true"
                  className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10 motion-safe:group-hover:scale-150 transition-transform duration-500"
                />
                <span className="relative block text-xs font-semibold uppercase tracking-wide text-orange-100 mb-2">
                  <Category q={q} language={language} />
                </span>
                <span className="relative block text-sm font-medium leading-relaxed text-white">
                  {text}
                </span>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          aria-label={language === "zh" ? "下一頁問題" : "Next questions"}
          disabled={page === pageCount - 1}
          onClick={() => setPage(page + 1)}
          className="p-2 rounded-lg text-black dark:text-white hover:bg-black/10 dark:hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronRightIcon />
        </button>
      </div>
      <div className="hidden md:flex justify-center mt-5" aria-live="polite">
        <span className="px-4 py-2 rounded-lg bg-orange-500/20 dark:bg-orange-500/30 border border-orange-500/40 text-sm font-semibold text-orange-600 dark:text-orange-400">
          {language === "zh"
            ? `第 ${page + 1} / ${pageCount} 頁`
            : `Page ${page + 1} / ${pageCount}`}
        </span>
      </div>
    </div>
  );
}
